import React from "react";
import { Card, Grid, Typography, Box, Divider, Chip } from "@mui/material";
import {
  Opacity, // Moisture
  Thermostat, // Temperature
  Science, // pH
  ElectricBolt, // Conductivity
  Grass, // Nitrogen
  Water, // Phosphorus
  SpaOutlined, // Potassium
  AccessTime, // Report time
} from "@mui/icons-material";

const SoilSensorValuesCard = ({ sensorValues }) => {
  // Define units and formatting
  const formatValue = (value, unit) => {
    return value ? `${value} ${unit}` : "N/A";
  };

  // Format the report time
  const formatReportTime = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Define sensor metadata
  const sensorMetadata = [
    {
      key: "humidity",
      label: "Humidity",
      unit: "%",
      icon: <Opacity color="primary" />,
      value: sensorValues?.humidity,
      color: "#3498db",
    },
    {
      key: "temperature",
      label: "Temperature",
      unit: "°C",
      icon: <Thermostat color="error" />,
      value: sensorValues?.temperature,
      color: "#e74c3c",
    },
    {
      key: "ph",
      label: "pH",
      unit: "",
      icon: <Science color="info" />,
      value: sensorValues?.ph,
      color: "#9b59b6",
    },
    {
      key: "conductivity",
      label: "Conductivity",
      unit: "μS/cm",
      icon: <ElectricBolt color="warning" />,
      value: sensorValues?.electric_conductivity,
      color: "#f39c12",
    },
    {
      key: "nitrogen",
      label: "Nitrogen",
      unit: "kg/ha",
      icon: <Grass color="success" />,
      value: sensorValues?.nitrogen,
      color: "#2ecc71",
    },
    {
      key: "phosphorus",
      label: "Phosphorus",
      unit: "kg/ha",
      icon: <Water color="primary" />,
      value: sensorValues?.phosphorus,
      color: "#3498db",
    },
    {
      key: "potassium",
      label: "Potassium",
      unit: "kg/ha",
      icon: <SpaOutlined color="secondary" />,
      value: sensorValues?.potassium,
      color: "#9b59b6",
    },
  ];

  return (
    <Card sx={{ p: 2, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Sensor Readings
        </Typography>
        <Chip
          icon={<AccessTime fontSize="small" />}
          label={`Reported: ${formatReportTime(sensorValues?.report_on)}`}
          variant="outlined"
          color="primary"
          size="small"
        />
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        {sensorMetadata.map((sensor) => (
          <Grid item xs={12} sm={6} md={4} key={sensor.key}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 1.5,
                borderRadius: 1,
                border: "1px solid #eee",
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                },
              }}
            >
              <Box sx={{ mr: 2 }}>{sensor.icon}</Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {sensor.label}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="medium"
                  sx={{ color: sensor.color }}
                >
                  {formatValue(sensor.value, sensor.unit)}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
};

export default SoilSensorValuesCard;
